import React, { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  requestRemoveImage,
  requestUploadImage,
  requestRemoveDynamicCmsImage,
  requestUploadDynamicCmsImage,
} from '../redux/sagas/requests/imageUploadService'
import ConfirmModal from './ConfirmModal'
import FormLabel from './form/FormLabel'
import { DangerButton, IconButton } from './CustomButton'
import { fileTypeFromBlob } from 'file-type'
import { toast } from 'react-toastify'
import { IMAGE_MAX_SIZE_IN_KB } from '../constants/commons'

export const ImageUploadCard = ({
                                  title,
                                  initialImage,
                                  permanentRemove = true,
                                  onImageUploaded,
                                  onImageRemoved,
                                  disabled = false,
                                  confirmation = true,
                                  isRequired,
                                  setCustomLoading,
                                  className = 'w-full mb-4',
                                  isCms = false,
                                  formLabelTrailing
                                }) => {
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)


  const [localImageFile, setLocalImageFile] = useState()

  const { getRootProps, isDragActive } = useDropzone(
    {
      disabled: disabled,
      accept: 'image/*',
      maxFiles: 1,
      onDropAccepted: acceptedFiles => {
        setLocalImageFile(acceptedFiles[0])
        uploadImageAction(acceptedFiles[0])
      },
      onDropRejected: (_) => {
        alert('Unsupported file type.')
      },
    })

  async function onImageBrowsed(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!event.target.files.length) {
      return
    }

    const image = event.target.files[0]


    const res = await fileTypeFromBlob(image)

    if (image.size > IMAGE_MAX_SIZE_IN_KB * 1024) {
      toast.error(`The file you uploaded exceeds maximum file size ${IMAGE_MAX_SIZE_IN_KB}KB.`)
    } else if (res.mime.includes('image')) {

      setLocalImageFile(image)
      uploadImageAction(image)
    } else {
      toast.error('File not supported.')
    }

  }

  const handleSetLoading = (loading) => {
    setLoading(loading)
    if(setCustomLoading !== undefined) {
      setCustomLoading(loading)
    }
  }

  function uploadImageAction(imageFile) {

    handleSetLoading(true)

    new Promise((resolve, reject) => {
      if (isCms) {
        resolve(requestUploadDynamicCmsImage(imageFile))
      } else {
        resolve(requestUploadImage(imageFile))
      }
    }).then((response) => {
      onImageUploaded(response.data.image)
      setLocalImageFile(undefined)
    }).catch((error) => {
      setError(error)
    }).finally(() => {
      handleSetLoading(false)
    })
  }

  function removeImageAction(e) {
    e?.stopPropagation()
    e?.preventDefault()
    if (!permanentRemove) {
      setShowModal(false)
      onImageRemoved('')
    } else {
      handleSetLoading(true)
      new Promise((resolve, reject) => {
        if (isCms) {
          resolve(requestRemoveDynamicCmsImage(initialImage))
        } else {
          resolve(requestRemoveImage(initialImage))
        }
      }).then((response) => {
        setShowModal(false)
        onImageRemoved('')
      }).catch((error) => {
        setError(error)
      }).finally(() => {
        handleSetLoading(false)
      })
    }
  }

  function reset() {
    setLocalImageFile()
    setTimeout(() => {
      setError(false)
    }, 0)
  }

  return (
    <>
      <div className={className}>
        <FormLabel label={title ?? 'Upload Image'} isRequired={isRequired} trailing={formLabelTrailing}/>
        <label
          className={`${isDragActive && 'bg-gray-100 border-blue-500'} bg-secondary-100 relative border-2 border-rand-100  border-dashed rounded-md h-20 flex justify-center items-center overflow-hidden`} {...getRootProps()}>
          {loading ?
            <div className="flex items-center justify-center py-4 space-x-2">
              <svg className="animate-spin h-7 w-7 text-secondary-500" xmlns="http://www.w3.org/2000/svg"
                   style={{ marginTop: 2, marginBottom: 1 }}
                   fill="none"
                   viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-sm">Uploading</p>
            </div> :
            error ?
              <div className="flex items-center justify-center w-full h-full px-4 py-3 cards">
                <div className="flex items-center justify-center w-full h-full space-x-2">
                  <div className="text-sm">
                    Error loading image
                  </div>
                  <DangerButton onClick={() => uploadImageAction(localImageFile)}>Retry</DangerButton>
                  {!disabled &&
                    <IconButton className="absolute top-3 right-3" onClick={reset} disabled={loading} />
                  }
                </div>
              </div> :
              initialImage?.length !== 0 ?
                // image display section
                <div className="flex items-center justify-center w-full h-full px-4 py-3 cards">
                  <div className="flex items-center w-full h-full pr-8">
                    <div className="mr-3 overflow-hidden bg-gray-100 w-28 h-14 rounded-md shrink-0">
                      <img className="object-contain w-full h-full" src={initialImage} alt="uploadedImage" />
                    </div>

                    <p className="h-8 text-xs text-2-clamp">
                      {initialImage}
                    </p>
                    {!!initialImage && !disabled &&
                      <IconButton className="absolute top-3 right-3"
                                  onClick={(e) => confirmation ? setShowModal(true) : removeImageAction(e)}
                                  disabled={loading} />
                    }
                  </div>
                </div>
                :
                // image upload section
                <div className="px-4 py-3 cursor-pointer">
                  <input type="file" className="hidden" accept="image/*" onChange={onImageBrowsed}
                         disabled={loading || disabled} />
                  <div className="flex items-center">
                    <span className="mr-2 text-5xl material-icons text-rand-300">
                    insert_photo
                    </span>
                    <p className="text-sm font-medium text-rand-100">Drag and Drop Image or <span
                      className="text-secondary-500">Click to Upload</span></p>
                  </div>
                </div>
          }
        </label>
      </div>

      <ConfirmModal title="Remove Image"
                    showModal={showModal}
                    setShowModal={() => setShowModal(false)}
                    children="Are you sure you want to remove image?"
                    disabled={loading}
                    onConfirm={removeImageAction} />
    </>
  )
}
